import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { toggle, isSaved, isLoggedIn, markLoggedOut, toggleGuest } from '@theme/wishlist';
import { trackWishlistEvent } from '@theme/wishlist-track';
import { absolute, productEventProps } from '@theme/klaviyo-client';
import { showSignInPrompt } from '@theme/wishlist-prompt';
import { announce } from '@theme/ca-announcer';
import { VariantSheet, bodyFromData } from '@theme/ca-variant-sheet';

const smallImage = (url) => {
  if (!url) return '';
  try {
    const resized = new URL(url, window.location.origin);
    resized.searchParams.set('width', '160');
    return resized.href;
  } catch {
    return url;
  }
};

class WishlistVariantSheet extends Component {
  heart = null;
  trigger = null;
  variants = [];
  defaultImage = '';
  session = 0;

  connectedCallback() {
    super.connectedCallback();
    const dialog = this.querySelector('dialog');
    if (!dialog) return;
    this.sheet = new VariantSheet(dialog, {
      policy: 'save',
      label: this.dataset.labelSave,
      onConfirm: (detail) => this.confirm(detail),
      onClose: () => this.closed(),
    });

    this.onExternalUpdate = (event) => {
      if (!this.sheet.isOpen) return;
      const target = event.detail?.productId;
      if (target === undefined || Number(target) === this.productId) this.sheet.refresh();
    };
    document.addEventListener(ThemeEvents.wishlistUpdate, this.onExternalUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.sheet?.destroy();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.onExternalUpdate);
  }

  get productId() {
    return Number(this.heart?.dataset.productId);
  }

  get listId() {
    return this.heart?.dataset.listId || undefined;
  }

  variantById(id) {
    return this.variants.find((variant) => variant.id === id);
  }

  liveTrigger() {
    if (this.trigger?.isConnected) return this.trigger;
    const productId = this.productId;
    const heart =
      (this.heart?.id && document.getElementById(this.heart.id)) ||
      (productId && document.querySelector(`wishlist-heart[data-product-id="${productId}"]`));
    return heart?.querySelector('button') ?? null;
  }

  money(cents) {
    const format = this.dataset.moneyFormat;
    if (!format || !Number.isFinite(cents)) return '';
    return formatMoney(cents, format, this.heart?.dataset.currency || 'USD');
  }

  openFor(heart, trigger) {
    const data = heart.variantData;
    const body = this.querySelector('template[data-variant-body]');
    const pill = this.querySelector('template[data-variant-pill]');
    if (!data || !this.sheet || !body || !pill) return false;

    this.session += 1;
    this.heart = heart;
    this.trigger = trigger ?? null;
    this.variants = data.v;
    this.defaultImage = smallImage(heart.dataset.image);

    const { productTitle = '', productUrl = '', variantId } = heart.dataset;
    const template = data.o ? this.dataset.labelPillsOne : this.dataset.labelPillsMany;
    this.sheet.open(
      bodyFromData(
        data,
        { body, pill },
        {
          title: productTitle,
          url: productUrl,
          image: this.defaultImage,
          variantId,
          money: (cents) => this.money(cents),
          label: (template || '').replace('%option%', () => data.o || '').replace('%title%', () => productTitle),
        }
      ),
      {
        trigger,
        confirmLabel: this.dataset.labelSave,
        removeLabel: this.dataset.labelRemove,
        isSaved: (savedPill) => isSaved(this.productId, Number(savedPill.dataset.variantId), this.listId),
      }
    );
    trigger?.setAttribute('aria-expanded', 'true');
    if (productTitle) this.sheet.dialog.setAttribute('aria-label', productTitle);

    const saved = [...this.sheet.dialog.querySelectorAll('[data-variant-pill][data-saved="true"]')];
    if (saved.length === 1) this.sheet.select(saved[0]);
    return true;
  }

  closed() {
    this.trigger?.setAttribute('aria-expanded', 'false');
    const live = this.liveTrigger();
    live?.setAttribute('aria-expanded', 'false');
    live?.focus({ preventScroll: true });
    this.trigger = null;
    this.heart = null;
  }

  async confirm({ variantId, pill, button }) {
    const variant = this.variantById(Number(variantId));
    const { heart, session } = this;
    if (!variant || !heart) return;

    const { dataset } = heart;
    const productId = Number(dataset.productId);
    const listId = dataset.listId || undefined;
    const handle = dataset.productHandle;
    const meta = { price: variant.p || null, source: dataset.source || 'other' };
    const image = absolute(variant.m || this.defaultImage);
    const wasSaved = pill?.dataset.saved === 'true';

    if (!isLoggedIn()) {
      const { inList, stored, full } = toggleGuest(productId, variant.id, handle, meta);
      if (full || !stored) {
        this.reportError(full ? 'list_full' : 'storage_unavailable');
        return;
      }
      if (inList) showSignInPrompt();
      this.finish(session, inList);
      return;
    }

    button.setAttribute('aria-disabled', 'true');

    try {
      const result = await toggle(productId, variant.id, listId, handle, meta);
      this.trackVariant(heart, variant, image, result.in_list ? 'Added to Wishlist' : 'Removed from Wishlist');
      this.finish(session, result.in_list);
    } catch (error) {
      if (error.code === 'login_required') {
        markLoggedOut();
        if (wasSaved) {
          if (this.session === session) this.sheet.close();
          return;
        }
        toggleGuest(productId, variant.id, handle, meta);
        showSignInPrompt();
        this.finish(session, true);
        return;
      }
      this.reportError(error.code, session);
      if (this.session === session) button.removeAttribute('aria-disabled');
    }
  }

  finish(session, saved) {
    const current = this.session === session;
    if (current) this.sheet.close();
    else if (this.sheet.isOpen) return;
    const message = saved ? this.dataset.labelAnnounceSaved : this.dataset.labelAnnounceRemoved;
    if (message) announce(message);
  }

  trackVariant(heart, variant, image, metric) {
    const { dataset } = heart;
    trackWishlistEvent(
      metric,
      productEventProps({
        productId: Number(dataset.productId),
        variantId: variant.id,
        productName: dataset.productTitle || '',
        variantName: variant.t || '',
        sku: variant.s || '',
        vendor: dataset.vendor || '',
        productType: dataset.productType || '',
        image,
        url: dataset.productUrl,
        price: variant.p,
        compareAt: variant.c,
        currency: dataset.currency || '',
        available: variant.a === 1,
        source: dataset.source || 'other',
      })
    );
  }

  reportError(code, session = this.session) {
    if (this.session !== session) return;
    const full = code === 'list_full' || code === 'wishlist_too_large';
    const message = full ? this.dataset.errorFull : this.dataset.errorGeneric;
    if (message) announce(message);
  }
}

if (!customElements.get('wishlist-variant-sheet')) {
  customElements.define('wishlist-variant-sheet', WishlistVariantSheet);
}
